import React, { useEffect, useState } from 'react';
import { Button, CardHeader, GlassCard, Icon, IconButton, Modal, ModalClose } from '../../campaign-os';
import { creditRows, creditsHero, formatMoney, toLocalInput, validateCreditForm } from '../data/walletSpend';
import { formatDateTime } from '../data/format';
import { useShellToast } from '../useShellToast';
import { getErrorMessage, surepassUsageService } from '../../../../services/apiService';

/**
 * The Surepass credits on the Wallet: what is left, what was recorded, and the two things an admin
 * ever does about it — set the balance the console shows, or add a top-up.
 *
 * Nothing here can read the Surepass wallet (no API offers it), so every figure is the arithmetic of
 * what an admin recorded less the calls this app made, at the rate card plus GST. The card says so.
 */

const FIELD =
  'w-full rounded-xl border border-cos-line bg-white/70 px-3 py-2.5 text-sm text-cos-ink placeholder:text-cos-ink-secondary/60 focus:border-cos-teal/50 focus:outline-none focus:ring-2 focus:ring-cos-teal/15 transition';
const FIELD_BAD = 'border-cos-status-coral/60 focus:border-cos-status-coral/60 focus:ring-cos-status-coral/15';
const LABEL = 'mb-1.5 block text-[13px] font-semibold text-cos-ink';
const HINT = 'mt-1 text-[12px] leading-[17px] text-cos-ink-secondary';
const ERROR = 'mt-1 text-[12px] font-medium leading-[17px] text-cos-status-coral';

const TONE = {
  teal: {
    value: 'text-cos-ink',
    bar: 'from-cos-teal-bright to-cos-teal-deep',
    chip: 'bg-cos-teal-faint text-cos-teal-deep',
    label: 'Healthy',
  },
  amber: {
    value: 'text-cos-status-amber',
    bar: 'from-[#D8B55F] to-cos-status-amber',
    chip: 'bg-cos-status-amber-bg text-cos-status-amber',
    label: 'Running low',
  },
  coral: {
    value: 'text-cos-status-coral',
    bar: 'from-[#CC8577] to-cos-status-coral',
    chip: 'bg-cos-status-coral-bg text-cos-status-coral',
    label: 'Overdrawn',
  },
};

const ALERT_STYLE = {
  amber: 'bg-cos-status-amber-bg text-cos-status-amber',
  coral: 'bg-cos-status-coral-bg text-cos-status-coral',
};

const KIND_ICON = {
  opening_balance: { icon: 'account_balance_wallet', wrap: 'bg-cos-surface-hover text-cos-teal-deep' },
  top_up: { icon: 'add_card', wrap: 'bg-cos-teal-faint text-cos-teal-deep' },
  adjustment: { icon: 'tune', wrap: 'bg-cos-beige-light text-cos-beige-ink' },
};

const AMOUNT_TONE = { ink: 'text-cos-ink', teal: 'text-cos-teal-deep', coral: 'text-cos-status-coral' };

const COPY = {
  opening_balance: {
    title: 'Set balance',
    icon: 'account_balance_wallet',
    lead: 'What the Surepass console shows right now. Calls recorded after this moment are subtracted from it.',
    amountLabel: 'Balance on the console (₹)',
    when: 'As of',
    whenHint: 'The moment you read that balance. Defaults to now.',
    done: 'Balance recorded',
  },
  top_up: {
    title: 'Add top-up',
    icon: 'add_card',
    lead: 'Credits you bought on the Surepass console. They are added to the balance from this moment.',
    amountLabel: 'Amount bought (₹)',
    when: 'Bought at',
    whenHint: 'Defaults to now.',
    done: 'Top-up recorded',
  },
};

function Figure({ figure }) {
  return (
    <div className="flex min-h-[74px] flex-col justify-center rounded-xl border border-cos-line-subtle bg-white/55 px-3 py-2.5">
      <div className="text-2xs font-semibold uppercase tracking-[0.08em] text-cos-ink-muted">{figure.label}</div>
      <div className="mono mt-1 text-[17px] font-bold leading-none tracking-tight text-cos-ink">{figure.value}</div>
      {figure.sub && <div className="mt-1 text-[11.5px] leading-tight text-cos-ink-secondary">{figure.sub}</div>}
    </div>
  );
}

/** The hero: remaining credits, the spend against them, and what an admin can do about it. */
export function CreditsCard({ view, loading, error, isAdmin, onRecord, onRetry }) {
  const hero = creditsHero(view?.balance ?? null);
  const tone = TONE[hero.tone];
  const empty = hero.state === 'empty';

  if (loading && !view) {
    return (
      <GlassCard accentRail>
        <div className="eyebrow text-cos-teal-deep/80">Surepass credits</div>
        <p className="py-6 text-sm text-cos-ink-secondary">Adding up what is left…</p>
      </GlassCard>
    );
  }

  if (error && !view) {
    return (
      <GlassCard accentRail>
        <div className="eyebrow text-cos-teal-deep/80">Surepass credits</div>
        <div className="flex flex-wrap items-center gap-3 py-4">
          <p className="text-sm text-cos-status-coral">{error}</p>
          <Button variant="secondary" size="sm" icon="refresh" onClick={onRetry}>
            Try again
          </Button>
        </div>
      </GlassCard>
    );
  }

  return (
    <GlassCard accentRail>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="eyebrow text-cos-teal-deep/80">Surepass credits</span>
          {!empty && <span className={`rounded-full px-2 py-0.5 text-2xs font-bold ${tone.chip}`}>{tone.label}</span>}
        </div>
        {isAdmin && !empty && (
          <div className="flex shrink-0 items-center gap-2">
            <Button variant="secondary" size="sm" icon="add_card" onClick={() => onRecord('top_up')}>
              Add top-up
            </Button>
            <Button variant="primary" size="sm" icon="account_balance_wallet" onClick={() => onRecord('opening_balance')}>
              Set balance
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,430px)] lg:gap-8">
        <div className="min-w-0">
          {empty ? (
            <div>
              <h3 className="max-w-[420px] text-[19px] font-bold leading-tight tracking-tight text-cos-ink">{hero.title}</h3>
              {isAdmin ? (
                <Button className="mt-3" variant="primary" icon="account_balance_wallet" onClick={() => onRecord('opening_balance')}>
                  Set balance
                </Button>
              ) : (
                <p className="mt-2 text-[13px] text-cos-ink-secondary">An admin records it from the Surepass console.</p>
              )}
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-baseline gap-2">
                <span className={`mono text-[42px] font-bold leading-none tracking-tight ${tone.value}`}>{hero.value}</span>
                <span className="text-[13px] font-semibold text-cos-ink-muted">remaining</span>
              </div>
              <div className="mt-4 h-2.5 overflow-hidden rounded-full bg-[rgba(70,80,72,0.10)]">
                <div
                  className={`h-full rounded-full bg-gradient-to-r ${tone.bar} transition-[width] duration-700`}
                  style={{ width: `${hero.progressPct > 0 ? Math.max(2, hero.progressPct) : 0}%` }}
                />
              </div>
              <div className="mt-1.5 flex items-center justify-between gap-3 text-2xs text-cos-ink-muted">
                <span className="mono">{hero.progressPct}% of the credits used</span>
                {hero.state !== 'ok' && view?.balance?.low_threshold_inr !== undefined && (
                  <span>Low under {formatMoney(view.balance.low_threshold_inr)}</span>
                )}
              </div>
            </>
          )}

          <p className="mt-3 max-w-[560px] text-[12px] leading-[18px] text-cos-ink-secondary">{hero.caption}</p>
          <p className="mt-1 max-w-[560px] text-[12px] leading-[18px] text-cos-ink-muted">{hero.note}</p>
        </div>

        {hero.figures.length > 0 && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {hero.figures.map((f) => (
              <Figure key={f.key} figure={f} />
            ))}
          </div>
        )}
      </div>

      {hero.alert && (
        <p className={`mt-4 flex items-start gap-2 rounded-xl px-3 py-2 text-[12.5px] leading-[18px] ${ALERT_STYLE[hero.alert.tone]}`}>
          <Icon name={hero.alert.tone === 'coral' ? 'priority_high' : 'error'} className="mt-px shrink-0 text-[17px]" weight={600} />
          <span>{hero.alert.text}</span>
        </p>
      )}
      {hero.warning && <p className="mt-2 text-[12px] leading-[18px] text-cos-status-amber">{hero.warning}</p>}
    </GlassCard>
  );
}

/** What was recorded, newest first. Long histories stay collapsed — the hero is the answer, this is the trail. */
export function CreditHistory({ view, isAdmin, onRemove }) {
  const rows = creditRows(view?.entries);
  const [all, setAll] = useState(false);
  if (!rows.length) return null;
  const shown = all ? rows : rows.slice(0, 5);

  return (
    <GlassCard padded={false}>
      <div className="px-5 pt-5">
        <CardHeader
          title="Credit history"
          subtitle="What was recorded, when it took effect, and who recorded it"
          icon={<Icon name="receipt_long" className="text-[20px] text-cos-teal-deep" />}
          action={
            rows.length > 5 ? (
              <Button variant="ghost" size="sm" onClick={() => setAll((v) => !v)}>
                {all ? 'Show less' : `Show all ${rows.length}`}
              </Button>
            ) : null
          }
        />
      </div>
      <ul className="divide-y divide-cos-line-subtle">
        {shown.map((r) => {
          const kind = KIND_ICON[r.kind] || KIND_ICON.adjustment;
          return (
            <li key={r.id} className="flex items-center gap-3 px-5 py-3">
              <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg ${kind.wrap}`}>
                <Icon name={kind.icon} className="text-[17px]" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[13px] font-semibold text-cos-ink">{r.kind_label}</span>
                  {!r.counted && (
                    <span
                      title="A later opening balance already includes this — it is not in the total"
                      className="rounded-full bg-cos-beige-light px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-cos-beige-ink"
                    >
                      superseded
                    </span>
                  )}
                </div>
                <div className="truncate text-[12px] leading-[17px] text-cos-ink-secondary">
                  {formatDateTime(r.effective_at)} · {r.who}
                  {r.note ? ` · ${r.note}` : ''}
                </div>
              </div>
              <span className={`mono shrink-0 text-[14px] font-semibold ${AMOUNT_TONE[r.tone]}`}>{r.amount}</span>
              {isAdmin && <IconButton icon="delete" size="sm" label={`Remove this ${r.kind_label.toLowerCase()}`} onClick={() => onRemove(r)} />}
            </li>
          );
        })}
      </ul>
    </GlassCard>
  );
}

/** Record an opening balance or a top-up. One amount, one moment, an optional note. */
export function CreditFormModal({ open, kind, onClose, onSaved }) {
  const toast = useShellToast();
  const copy = COPY[kind] || COPY.top_up;
  const [amount, setAmount] = useState('');
  const [when, setWhen] = useState(() => toLocalInput(new Date()));
  const [maxWhen, setMaxWhen] = useState(() => toLocalInput(new Date()));
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const now = toLocalInput(new Date());
    setAmount('');
    setWhen(now);
    setMaxWhen(now);
    setNote('');
    setErrors({});
    setBusy(false);
  }, [open, kind]);

  const submit = async () => {
    const checked = validateCreditForm({ kind, amount, when, note }, new Date());
    setErrors(checked.errors);
    if (!checked.payload) return;
    setBusy(true);
    try {
      await surepassUsageService.addCredit(checked.payload);
      toast(copy.done, { tone: 'success' });
      onSaved();
      onClose();
    } catch (err) {
      toast(getErrorMessage(err) || 'Could not record that', { tone: 'coral' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={() => !busy && onClose()} size="md" labelledBy="credit-form-title">
      <div className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-cos-teal-faint">
              <Icon name={copy.icon} className="text-[21px] text-cos-teal-deep" weight={500} />
            </span>
            <div>
              <h2 id="credit-form-title" className="text-[17px] font-bold tracking-tight text-cos-ink">
                {copy.title}
              </h2>
              <p className="mt-1 max-w-[420px] text-[13px] leading-[19px] text-cos-ink-secondary">{copy.lead}</p>
            </div>
          </div>
          <ModalClose onClose={() => !busy && onClose()} />
        </div>

        <div className="mt-5 flex flex-col gap-4">
          <div>
            <label className={LABEL} htmlFor="credit-amount">
              {copy.amountLabel}
            </label>
            <input
              id="credit-amount"
              className={`${FIELD} mono ${errors.amount ? FIELD_BAD : ''}`}
              inputMode="decimal"
              autoFocus
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
            />
            {errors.amount && <p className={ERROR}>{errors.amount}</p>}
          </div>

          <div>
            <label className={LABEL} htmlFor="credit-when">
              {copy.when}
            </label>
            <input
              id="credit-when"
              type="datetime-local"
              className={`${FIELD} ${errors.when ? FIELD_BAD : ''}`}
              value={when}
              max={maxWhen}
              onChange={(e) => setWhen(e.target.value)}
            />
            {errors.when ? <p className={ERROR}>{errors.when}</p> : <p className={HINT}>{copy.whenHint}</p>}
          </div>

          <div>
            <label className={LABEL} htmlFor="credit-note">
              Note <span className="font-normal text-cos-ink-muted">(optional)</span>
            </label>
            <input
              id="credit-note"
              className={`${FIELD} ${errors.note ? FIELD_BAD : ''}`}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Invoice number, who bought it…"
            />
            {errors.note && <p className={ERROR}>{errors.note}</p>}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" icon={busy ? 'hourglass_top' : copy.icon} onClick={submit} disabled={busy}>
            {busy ? 'Recording…' : copy.title}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** Removing an entry changes the balance, so it is asked for first. */
export function RemoveCreditModal({ entry, onClose, onRemoved }) {
  const toast = useShellToast();
  const [busy, setBusy] = useState(false);

  const remove = async () => {
    setBusy(true);
    try {
      await surepassUsageService.removeCredit(entry.id);
      toast('Credit entry removed', { tone: 'success' });
      onRemoved();
      onClose();
    } catch (err) {
      toast(getErrorMessage(err) || 'Could not remove that entry', { tone: 'coral' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={Boolean(entry)} onClose={() => !busy && onClose()} size="md" labelledBy="credit-remove-title">
      <div className="p-6">
        <div className="flex items-start gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-cos-status-coral-bg">
            <Icon name="delete" className="text-[21px] text-cos-status-coral" />
          </span>
          <div className="min-w-0">
            <h2 id="credit-remove-title" className="text-base font-bold text-cos-ink">
              Remove this {entry?.kind_label?.toLowerCase()}?
            </h2>
            <p className="mt-1 text-sm leading-[21px] text-cos-ink-secondary">
              <span className="mono font-semibold text-cos-ink">{entry?.amount}</span> from {entry ? formatDateTime(entry.effective_at) : ''} stops
              counting and the balance is worked out again. The record is kept, not deleted.
            </p>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="danger" icon="delete" onClick={remove} disabled={busy}>
            {busy ? 'Removing…' : 'Remove entry'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
